import React, { useEffect, useState, useRef } from 'react';
import { Photo, getImageByIdSync } from '../../services/photoService';

interface ImageProps {
  id?: string;
  src?: string;
  alt: string;
  className?: string;
  placeholderText?: string;
  width?: string | number;
  height?: string | number;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  /**
   * The image file's own pixel size, emitted as width/height attributes so the
   * browser can reserve the box before the file arrives. Layout is still set by
   * CSS and the inline style; use it on hero images.
   */
  intrinsicWidth?: number;
  intrinsicHeight?: number;
  /** Native lazy loading for images far below the fold (e.g. the footer). */
  loading?: 'lazy' | 'eager';
}

const Image: React.FC<ImageProps> = ({ 
  id, 
  src, 
  alt, 
  className = '', 
  placeholderText, 
  width, 
  height,
  objectFit = 'contain',
  intrinsicWidth,
  intrinsicHeight,
  loading
}) => {
  // Resolved synchronously from the build-time registry, so the very first
  // render — including the prerendered HTML — already has the real image.
  // It used to start empty and look the image up in an effect, which meant the
  // HTML shipped a grey placeholder, the browser then blanked it while the
  // registry downloaded, and only then started fetching the actual picture.
  const resolve = (imageId?: string) => {
    const found = imageId ? getImageByIdSync(imageId) : null;
    return found && found.url ? found : null;
  };
  const [initial] = useState(() => resolve(id));
  const [image, setImage] = useState<Photo | null>(initial);
  const [fetchError, setFetchError] = useState<boolean>(!!id && !initial);
  const [imgLoadError, setImgLoadError] = useState<boolean>(false);
  const fallbackAttempted = useRef(false);
  const lastId = useRef(id);

  // Only re-resolve when the id actually changes after mount; the initial
  // value already came from the lazy state initialisers above.
  useEffect(() => {
    if (lastId.current === id) return;
    lastId.current = id;
    const found = resolve(id);
    if (id && !found) {
      console.error(`Image.tsx: no image registered with id "${id}"`);
    }
    setImage(found);
    setFetchError(!!id && !found);
    setImgLoadError(false);
    fallbackAttempted.current = false;
  }, [id]);

  const handleInitialImgError = () => {
    if (!fallbackAttempted.current) {
        console.error(`Image.tsx: Failed to load actual image file for ID "${id}" from URL "${image?.url}". Attempting fallback.`);
        setImgLoadError(true);
    }
  };
  
  // Common style properties for consistent sizing
  const styleProps = {
    width: width || '100%',
    height: height || '100%',
    objectFit: objectFit as any
  };

  // If we have a direct src, use that
  if (src) {
    return <img 
      src={src} 
      alt={alt} 
      className={className} 
      style={styleProps} 
      loading={loading}
      decoding={loading === 'lazy' ? 'async' : undefined}
      onError={(e) => {
        (e.target as HTMLElement).outerHTML = `<div style="width:${styleProps.width};height:${styleProps.height};border:1px solid red;display:flex;align-items:center;justify-content:center;background:#fee;color:#c00;">Failed: ${alt}</div>`;
      }}
    />;
  }

  // If we're loading an image by ID
  if (id) {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.log(
        `Image.tsx Debug ID: ${id}`,
        `| FetchError: ${fetchError}`,
        `| ImgLoadError: ${imgLoadError}`,
        `| Image State: ${JSON.stringify(image)}`,
        `| PlaceholderText: "${placeholderText}"`,
        `| Alt: "${alt}"`
      );
    }


    if (fetchError || !image || !image.url || imgLoadError) {
      return (
        <div 
          className={`img-placeholder error-placeholder ${className}`}
          style={{
            ...styleProps,
            border: '1px solid #ddd',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            fontSize: width && typeof width === 'number' && width < 50 ? '8px' : '12px',
            color: '#999',
            overflow: 'hidden',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
        >
          {placeholderText || alt || 'Image not available'}
        </div>
      );
    }

    return <img 
      src={image.url} 
      // The caller's alt is localized (t(...)); the registry altText is English
      // only, so it is just the fallback.
      alt={alt || image.altText} 
      className={className}
      width={intrinsicWidth}
      height={intrinsicHeight}
      style={{
        ...styleProps
      }}
      onError={handleInitialImgError}
    />;
  }

  // Fallback if no id and no src
  return (
    <div 
      className={`img-placeholder no-id-placeholder ${className}`}
      style={{
        ...styleProps,
        border: '1px dashed #aaa',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}
    >
      {placeholderText || alt}
    </div>
  );
};

export default Image; 